import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { borderPrimary, rounded, semiBoldText, regularText, whiteBG, primaryText, largeTextSize, smallTextSize, greyTextColor, secondaryText, baseTextSize, mediumText } from '../../styling/stylingHelper';
import { normalizeDate } from '../../utils/dateUtils';

interface CustomDatePickerProps {
  initialStartDate: Date;
  initialEndDate: Date;
  onApply: (startDate: Date, endDate: Date) => void;
  onCancel: () => void;
  onClose: () => void;
  className?: string;
  align?: 'left' | 'right' | 'center'; // <-- Also allow 'center'
}

const formatDate = (date: Date, options: Intl.DateTimeFormatOptions) => {
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

/**
 * Returns calendar array for the given month/year:
 * - Prefixes with previous month dates to fill the first week (if needed)
 * - Appends next month dates to finish the last week (if needed)
 * - Does NOT pad to exactly 6 weeks (removes unnecessary trailing weeks of greyed out dates)
 */
const getCalendarArray = (year: number, month: number): { date: Date; isCurrentMonth: boolean }[] => {
  const calendarDays: { date: Date; isCurrentMonth: boolean }[] = [];
  const firstDay = new Date(year, month, 1);
  const startingDayOfWeek = firstDay.getDay(); // 0 - Sunday, 1 - Monday, etc.
  // We want Monday as the first column, so shift accordingly
  let blanks = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

  // Add suffix greyed out from last month if needed
  let prevDate = new Date(firstDay);
  prevDate.setDate(0); // Last day of previous month
  for (let i = 0; i < blanks; i++) {
    calendarDays.unshift({ date: new Date(prevDate), isCurrentMonth: false });
    prevDate.setDate(prevDate.getDate() - 1);
  }

  // Main current month dates
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({ date: new Date(year, month, day), isCurrentMonth: true });
  }

  // Add trailing from next month to fill the week
  const total = calendarDays.length;
  const daysToAdd = (7 - (total % 7)) % 7;
  let nextDate = new Date(year, month + 1, 1);
  for (let i = 0; i < daysToAdd; i++) {
    calendarDays.push({ date: new Date(nextDate), isCurrentMonth: false });
    nextDate.setDate(nextDate.getDate() + 1);
  }

  return calendarDays;
};

// Helper: Compare two dates if year, month, and day are equal ("calendar-day equality")
const isSameCalendarDay = (d1: Date, d2: Date) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  initialStartDate,
  initialEndDate,
  onApply,
  onCancel,
  onClose,
  className,
  align = 'right',
}) => {
  const { t } = useTranslation();

  // Normalize dates on initialization
  const normalizedStart = normalizeDate(initialStartDate);
  const normalizedEnd = normalizeDate(initialEndDate);

  const [tempStart, setTempStart] = useState(normalizedStart);
  const [tempEnd, setTempEnd] = useState(normalizedEnd);
  const [currentMonth, setCurrentMonth] = useState(normalizedEnd);
  const [selectionStep, setSelectionStep] = useState<null | 'first' | 'second'>(null);
  const pickerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const normalizedStart = normalizeDate(initialStartDate);
    const normalizedEnd = normalizeDate(initialEndDate);
    setTempStart(normalizedStart);
    setTempEnd(normalizedEnd);
    setCurrentMonth(normalizedEnd);
    setSelectionStep(null);
  }, [initialStartDate, initialEndDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleDayClick = useCallback((date: Date) => {
    const clickedDate = normalizeDate(date);
    const today = normalizeDate(new Date());

    // Don't allow selecting future dates
    if (clickedDate > today) {
      return;
    }

    if (selectionStep === null || selectionStep === 'second') {
      // Start a new selection - both start and end are the clicked date
      setTempStart(clickedDate);
      setTempEnd(clickedDate);
      setSelectionStep('first');

      // Update currentMonth to clicked date's month (recommended for intuitive UX)
      setCurrentMonth(new Date(clickedDate.getFullYear(), clickedDate.getMonth(), 1));
    } else if (selectionStep === 'first') {
      // Second selection: set as end, compute correct order
      let newStart = tempStart;
      let newEnd = clickedDate;
      if (newStart > newEnd) {
        [newStart, newEnd] = [newEnd, newStart];
      }
      setTempStart(newStart);
      setTempEnd(newEnd);
      setSelectionStep('second');
    }
  }, [tempStart, selectionStep]);

  const handleApply = () => {
    onApply(tempStart, tempEnd);
    setSelectionStep(null);
  };

  const handleCancel = () => {
    const normalizedStart = normalizeDate(initialStartDate);
    const normalizedEnd = normalizeDate(initialEndDate);
    setTempStart(normalizedStart);
    setTempEnd(normalizedEnd);
    setSelectionStep(null);
    onCancel();
  };

  // Only border left, no bottom
  // Expanded alignment logic to include "center"
  let dropdownContainer: string;
  if (align === 'left') {
    dropdownContainer = cn(
      'absolute top-full mt-2 w-[95vw] max-w-[330px] z-10 bg-white dark:bg-background-dark shadow-lg rounded-xl border',
      'border-borderPrimary dark:border-borderPrimary-dark',
      'left-0 max-[650px]:left-1/2 max-[650px]:-translate-x-1/2'
    );
  } else if (align === 'center') {
    dropdownContainer = cn(
      'absolute top-full mt-2 w-[95vw] max-w-[330px] z-10 bg-white dark:bg-background-dark shadow-lg rounded-xl border',
      'border-borderPrimary dark:border-borderPrimary-dark',
      'left-0 max-[550px]:left-1/2 max-[550px]:-translate-x-1/2'
    );
  } else { // align === 'right'
    dropdownContainer = cn(
      'absolute top-full mt-2 w-[95vw] max-w-[330px] z-10 bg-white dark:bg-background-dark shadow-lg rounded-xl border',
      'border-borderPrimary dark:border-borderPrimary-dark',
      'right-0 max-[650px]:left-1/2 max-[650px]:-translate-x-1/2'
    );
  }

  // Get now date for rendering 'today'
  const today = new Date();

  return (
    <div ref={pickerRef} className={cn(dropdownContainer, className)}>
      <div className="p-4">
        <div className="flex justify-between items-start mb-2 relative">
          <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>{t('common.customDateRange')}</h2>
          <button
            onClick={onClose}
            className={cn(greyTextColor, largeTextSize, "mb-[-4px]")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Month navigator */}
        <div className="flex justify-center items-center mb-3">
          <button
            onClick={() => {
              const newMonth = new Date(currentMonth);
              newMonth.setMonth(newMonth.getMonth() - 1);
              setCurrentMonth(newMonth);
            }}
            className={cn(whiteBG, borderPrimary, greyTextColor)}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span
            className={cn(semiBoldText, baseTextSize, secondaryText, "mx-2 px-2 py-1")}
            style={{ width: 200, display: "inline-block", textAlign: "center" }}
          >
            {formatDate(currentMonth, { month: 'long', year: 'numeric' })}
          </span>
          <button
            onClick={() => {
              const newMonth = new Date(currentMonth);
              newMonth.setMonth(newMonth.getMonth() + 1);
              const today = new Date();
              // Don't allow going to future months
              if (newMonth.getFullYear() > today.getFullYear() ||
                  (newMonth.getFullYear() === today.getFullYear() && newMonth.getMonth() > today.getMonth())) {
                return;
              }
              setCurrentMonth(newMonth);
            }}
            disabled={(() => {
              const nextMonth = new Date(currentMonth);
              nextMonth.setMonth(nextMonth.getMonth() + 1);
              const today = new Date();
              return nextMonth.getFullYear() > today.getFullYear() ||
                     (nextMonth.getFullYear() === today.getFullYear() && nextMonth.getMonth() > today.getMonth());
            })()}
            className={cn(
              whiteBG,
              borderPrimary,
              greyTextColor,
              (() => {
                const nextMonth = new Date(currentMonth);
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                const today = new Date();
                const isDisabled = nextMonth.getFullYear() > today.getFullYear() ||
                       (nextMonth.getFullYear() === today.getFullYear() && nextMonth.getMonth() > today.getMonth());
                return isDisabled ? "opacity-50" : "";
              })()
            )}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Date range inputs */}
        <div className="flex items-center gap-2 mb-4">
          <div className={cn("flex-1", rounded, whiteBG, "p-3", primaryText, baseTextSize, "border", borderPrimary, "cursor-pointer")}>
            <input
              type="text"
              className="w-full bg-transparent dark:bg-transparent outline-none"
              value={formatDate(tempStart, { month: 'short', day: 'numeric', year: 'numeric' })}
              readOnly
              // No field focus logic now, focus/step is mouse-driven
            />
          </div>
          <span className={cn(greyTextColor, baseTextSize)}>–</span>
          <div className={cn("flex-1", rounded, whiteBG, "p-3", primaryText, baseTextSize, "border", borderPrimary, "cursor-pointer")}>
            <input
              type="text"
              className="w-full bg-transparent dark:bg-transparent outline-none"
              value={formatDate(tempEnd, { month: 'short', day: 'numeric', year: 'numeric' })}
              readOnly
              // No field focus logic now, focus/step is mouse-driven
            />
          </div>
        </div>

        {/* Calendar */}
        <div className="mb-6">
          <div className="grid grid-cols-7 gap-1 text-center mb-2">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'].map((day) => (
              <div key={day} className={cn(smallTextSize, mediumText, secondaryText, "py-1")}>
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {getCalendarArray(currentMonth.getFullYear(), currentMonth.getMonth()).map((item, index) => {
              const normalizedItemDate = normalizeDate(item.date);
              const isFuture = normalizedItemDate > normalizeDate(today);
              const isSelected =
                (isSameCalendarDay(tempStart, tempEnd) && isSameCalendarDay(normalizedItemDate, tempStart)) ||
                isSameCalendarDay(normalizedItemDate, tempStart) ||
                isSameCalendarDay(normalizedItemDate, tempEnd);
              // Additionally, highlight in-range days if a range is selected
              const isInRange = !isSameCalendarDay(tempStart, tempEnd) &&
                normalizedItemDate > tempStart && normalizedItemDate < tempEnd;

              // Detect if today
              const isToday = isSameCalendarDay(item.date, today);

              return (
                <button
                  key={index}
                  type="button"
                  disabled={isFuture}
                  className={cn(regularText, smallTextSize,
                    "aspect-square rounded-full transition-colors flex items-center justify-center",
                    !item.isCurrentMonth && greyTextColor,
                    isFuture ? "opacity-40" : "hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark",
                    isSelected
                      ? "bg-darkGreenColor text-white font-medium hover:bg-darkGreenColor/90"
                      : isInRange
                      ? "bg-cardLightGreen dark:bg-cardLightGreen-dark text-textPrimary dark:text-textPrimary-dark"
                      : whiteBG
                  )}
                  onClick={() => handleDayClick(item.date)}
                  style={{ position: 'relative' }}
                >
                  <span className="block">{item.date.getDate()}</span>
                  {isToday && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: '4px',
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        background: '#0E8965',
                      }}
                    ></span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        {/* Edge-to-edge border above actions */}
        <div className="-mx-4 mt-4">
          <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
          <div className="flex justify-end gap-3 pt-4 px-4">
            <button
              onClick={handleCancel}
              className={cn(
                smallTextSize,
                secondaryText,
                semiBoldText,
                rounded,
                "px-4 py-3",
                whiteBG,
                "border",
                borderPrimary,
                "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark",
                "flex-1"
              )}
              style={{ minWidth: 0 }}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleApply}
              className={cn(smallTextSize, "text-white dark:text-white", semiBoldText, "bg-darkGreenColor dark:bg-darkGreenColor-dark", rounded,
              "px-4 py-3",
              "hover:bg-darkGreenColor/95",
              "flex-1")}
              style={{ minWidth: 0 }}
            >
              {t('common.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};